import Phaser from 'phaser';

type PuckState = 'on' | 'off';

interface PuckPalette {
  body: number;
  rim: number;
  textColor: string;
  innerRing: number;
  highlight: string; // CSS color
}

const PALETTES: Record<PuckState, PuckPalette> = {
  on: {
    body: 0xf7f7f5,
    rim: 0x121212,
    textColor: '#121212',
    innerRing: 0x121212,
    highlight: 'rgba(0,0,0,0.12)',
  },
  off: {
    body: 0x141414,
    rim: 0xf7f7f5,
    textColor: '#f7f7f5',
    innerRing: 0xf7f7f5,
    highlight: 'rgba(255,255,255,0.18)',
  },
};

const PUCK_TEXTURE_RADIUS = 64;
const SHADOW_OFFSET_Y = 4;
const SHADOW_PADDING = 8;
const TEXTURE_SIZE = (PUCK_TEXTURE_RADIUS + SHADOW_PADDING) * 2;

function hexToCss(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

function drawPuck(ctx: CanvasRenderingContext2D, palette: PuckPalette, label: string) {
  const cx = TEXTURE_SIZE / 2;
  const cy = TEXTURE_SIZE / 2;
  const r = PUCK_TEXTURE_RADIUS;

  // Drop shadow.
  ctx.fillStyle = 'rgba(0,0,0,0.4)';
  ctx.beginPath();
  ctx.arc(cx, cy + SHADOW_OFFSET_Y, r, 0, Math.PI * 2);
  ctx.fill();

  // Body.
  ctx.fillStyle = hexToCss(palette.body);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // Outer contrast rim (thicker — reads as the rim of the disc).
  ctx.strokeStyle = hexToCss(palette.rim);
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 2, 0, Math.PI * 2);
  ctx.stroke();

  // Bevel highlight just inside the rim.
  ctx.strokeStyle = palette.highlight;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 6, 0, Math.PI * 2);
  ctx.stroke();

  // Decorative inset ring around the label.
  ctx.strokeStyle = hexToCss(palette.innerRing);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.66, 0, Math.PI * 2);
  ctx.stroke();

  // Four small dots at cardinal positions, classic casino-puck look.
  const dotR = r * 0.06;
  const dotDist = r * 0.82;
  ctx.fillStyle = hexToCss(palette.innerRing);
  for (const a of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
    ctx.beginPath();
    ctx.arc(cx + dotDist * Math.cos(a), cy + dotDist * Math.sin(a), dotR, 0, Math.PI * 2);
    ctx.fill();
  }

  // Label.
  const fontPx = Math.round(r * (label.length === 2 ? 0.55 : 0.45));
  ctx.fillStyle = palette.textColor;
  ctx.font = `900 ${fontPx}px "Arial Black", Helvetica, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const m = ctx.measureText(label);
  const visualOffset = (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  ctx.fillText(label, cx, cy + visualOffset);
}

function puckTextureKey(state: PuckState): string {
  return `puck-${state}`;
}

function getPuckTexture(scene: Phaser.Scene, state: PuckState): string {
  const key = puckTextureKey(state);
  if (scene.textures.exists(key)) return key;
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Puck canvas 2D context unavailable');
  drawPuck(ctx, PALETTES[state], state.toUpperCase());
  scene.textures.addCanvas(key, canvas);
  return key;
}

export class Puck extends Phaser.GameObjects.Container {
  private sprite: Phaser.GameObjects.Sprite;
  private displayRadius: number;

  constructor(scene: Phaser.Scene, x: number, y: number, scale = 1) {
    super(scene, x, y);
    this.displayRadius = 22 * scale;
    const key = getPuckTexture(scene, 'off');
    this.sprite = scene.add.sprite(0, 0, key);
    this.sprite.setScale(this.displayRadius / PUCK_TEXTURE_RADIUS);
    this.add(this.sprite);
    scene.add.existing(this);
  }

  /**
   * Flip the puck to OFF and optionally slide it to (x, y) — the off
   * resting position, conventionally inside the don't-come wedge. Without
   * coords the puck just changes texture in place (used by the tutorial
   * scene which manages position itself).
   *
   * @param animate When true (default) the puck tweens; false snaps the
   *                position (used by hydrate where no roll motivates motion).
   */
  setOff(x?: number, y?: number, options: { animate?: boolean } = {}) {
    const animate = options.animate ?? true;
    this.sprite.setTexture(getPuckTexture(this.scene, 'off'));
    this.scene.tweens.killTweensOf(this);
    if (x === undefined || y === undefined) return;
    if (animate) {
      this.scene.tweens.add({
        targets: this,
        x, y,
        duration: 400,
        ease: 'Cubic.Out',
      });
    } else {
      this.x = x;
      this.y = y;
    }
  }

  /**
   * @param animate When true (default) the puck tweens to (x, y) over 400ms.
   *                When false, position is snapped — used by the bridge after
   *                engine.hydrate, where there's no live roll to motivate
   *                the animation.
   */
  setOn(_point: number, x: number, y: number, options: { animate?: boolean } = {}) {
    const animate = options.animate ?? true;
    this.sprite.setTexture(getPuckTexture(this.scene, 'on'));
    this.scene.tweens.killTweensOf(this);
    if (animate) {
      this.scene.tweens.add({
        targets: this,
        x, y,
        duration: 400,
        ease: 'Cubic.Out',
      });
    } else {
      this.x = x;
      this.y = y;
    }
  }
}
